import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Loading } from '../../ui';
import { useGiftSelection } from './useGiftSelection';
import { useGetStoreCategoriesQuery } from '../categories/categoriesApi';
import { getApiStoreName } from './mockStores';
import GiftSelectionLayout from './GiftSelectionLayout';

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
`;

/**
 * Filter courses to show only:
 * - type === 1 (courses, not packages)
 * - public is truthy (published to store)
 * - draft is falsy (not in draft mode)
 */
const filterAvailableCourses = (items) => {
  if (!items || !Array.isArray(items)) return [];
  return items.filter((item) => item.type === 1 && !!item.public && !item.draft);
};

/**
 * GiftSelection - Main container component
 *
 * URL format: /gift-select/:storeName?code=CODE&type=gift|invitation
 *
 * Path params:
 * - storeName: store name for branding and API lookups
 *
 * Query params:
 * - code: the gift code or invitation hash
 * - type: 'gift' (default) or 'invitation'
 *
 * Flow:
 * 1. User lands on page → sees courses immediately (no validation blocking)
 * 2. User browses and selects a course
 * 3. User confirms → code is validated AND course is redeemed
 */
const GiftSelection = () => {
  const { code, codeType, store, storeName, validationError, isLoading, isProcessing, handleSelectCourse, handleConfirmSelection, clearError } =
    useGiftSelection();

  // Fetch categories for the store (use real API store name for mock stores)
  const apiStoreName = getApiStoreName(storeName);
  const { data: categories, isLoading: isCategoriesLoading } = useGetStoreCategoriesQuery(apiStoreName, { skip: !apiStoreName });

  // Track confirmation errors to show in UI
  const [confirmError, setConfirmError] = useState(null);

  // Track selected category for filtering
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Prepare gift data (basic info from URL, no validation data yet)
  const giftData = {
    code,
    hash: codeType === 'invitation' ? code : null,
    codeType,
  };

  // Use real courses from store data, filtered by type/public/draft
  const allCourses = useMemo(() => filterAvailableCourses(store?.items), [store?.items]);

  // Filter courses by selected category
  const availableCourses = useMemo(() => {
    if (!selectedCategory) return allCourses;
    return allCourses.filter((course) => course.categories?.some((cat) => cat.id === selectedCategory));
  }, [allCourses, selectedCategory]);

  // Extract unique categories from courses
  const courseCategories = useMemo(() => {
    if (!categories) return [];
    // Filter to only show categories that have courses
    const courseCategoryIds = new Set();
    allCourses.forEach((course) => {
      if (course.categories) course.categories.forEach((cat) => courseCategoryIds.add(cat.id));
    });
    return categories.filter((cat) => courseCategoryIds.has(cat.id));
  }, [categories, allCourses]);

  // Show loading only while fetching store data for branding
  if (isLoading) {
    return (
      <PageWrapper>
        <Loading fullscreen />
      </PageWrapper>
    );
  }

  // Mock store for demo if not available from API
  const storeData = store?.owner
    ? store
    : {
        name: 'Demo Store',
        storeName: 'demo-store',
        theme: {
          color: 'teal',
        },
        owner: {
          name: 'Demo Store',
        },
      };

  // Wrap confirm handler to capture errors for UI display
  const handleConfirm = async (course) => {
    setConfirmError(null);
    clearError();

    const result = await handleConfirmSelection(course);

    if (!result.success) {
      setConfirmError(result.error);
    }

    return result;
  };

  return (
    <PageWrapper>
      <GiftSelectionLayout
        store={storeData}
        availableCourses={availableCourses}
        categories={courseCategories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        onSelectCourse={handleSelectCourse}
        onConfirmSelection={handleConfirm}
        giftData={giftData}
        isLoading={isProcessing || isCategoriesLoading}
        error={confirmError || validationError}
      />
    </PageWrapper>
  );
};

export default GiftSelection;
